import React, { useState, useRef, useEffect } from 'react';
import { bool, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { IconShare } from '../../components';

import css from './ShareButton.module.css';

/**
 * ShareButton component - Comprehensive sharing solution
 * 
 * Features:
 * - Native Web Share API for mobile devices (opens system share sheet)
 * - Desktop share menu with multiple options:
 *   - Copy link to clipboard
 *   - Email sharing
 *   - WhatsApp
 *   - Facebook
 *   - LinkedIn
 *   - QR Code generation
 * - Toast notifications for user feedback
 * - Finnish localization
 */
const ShareButton = props => {
  const { className, rootClassName, title, description, url } = props;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const menuRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleButtonClick = async () => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;
    const shareText = description || shareTitle;

    // On mobile with Web Share API, use native sharing
    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        // Don't show toast for successful native share (system handles feedback)
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
          showToastMessage('ShareButton.error', true);
        }
      }
    } else {
      // On desktop, toggle share menu
      setShowMenu(!showMenu);
    }
  };

  const handleCopyLink = async () => {
    const shareUrl = url || window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToastMessage('ShareButton.copied', false);
      setShowMenu(false);
    } catch (err) {
      console.error('Copy failed:', err);
      showToastMessage('ShareButton.copyError', true);
    }
  };

  const handleEmail = () => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`Katso tämä: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShowMenu(false);
  };

  const handleWhatsApp = () => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;
    const text = encodeURIComponent(`${shareTitle} - ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowMenu(false);
  };

  const handleFacebook = () => {
    const shareUrl = url || window.location.href;
    const fbUrl = encodeURIComponent(shareUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${fbUrl}`, '_blank', 'width=600,height=400');
    setShowMenu(false);
  };

  const handleLinkedIn = () => {
    const shareUrl = url || window.location.href;
    const linkedInUrl = encodeURIComponent(shareUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${linkedInUrl}`, '_blank', 'width=600,height=400');
    setShowMenu(false);
  };

  const handleQRCode = () => {
    setShowQRCode(true);
    setShowMenu(false);
  };

  const showToastMessage = (messageId, isError = false) => {
    setToastMessage(messageId);
    setToastError(isError);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  return (
    <>
      <div className={css.wrapper} ref={menuRef}>
        <button
          type="button"
          className={classes}
          onClick={handleButtonClick}
          title="Jaa"
          aria-label="Jaa tämä yritysprofiili"
        >
          <IconShare className={css.icon} />
          <span className={css.text}>
            <FormattedMessage id="ShareButton.share" />
          </span>
        </button>
        
        {showMenu && (
          <div className={css.shareMenu}>
            <button className={css.menuItem} onClick={handleCopyLink}>
              <span className={css.menuIcon}>🔗</span>
              <FormattedMessage id="ShareButton.copyLink" />
            </button>
            <button className={css.menuItem} onClick={handleEmail}>
              <span className={css.menuIcon}>✉️</span>
              <FormattedMessage id="ShareButton.email" />
            </button>
            <button className={css.menuItem} onClick={handleWhatsApp}>
              <span className={css.menuIcon}>💬</span>
              <FormattedMessage id="ShareButton.whatsapp" />
            </button>
            <button className={css.menuItem} onClick={handleFacebook}>
              <span className={css.menuIcon}>👥</span>
              <FormattedMessage id="ShareButton.facebook" />
            </button>
            <button className={css.menuItem} onClick={handleLinkedIn}>
              <span className={css.menuIcon}>💼</span>
              <FormattedMessage id="ShareButton.linkedin" />
            </button>
            <button className={css.menuItem} onClick={handleQRCode}>
              <span className={css.menuIcon}>📱</span>
              <FormattedMessage id="ShareButton.qrcode" />
            </button>
          </div>
        )}
      </div>

      {showQRCode && (
        <div className={css.qrModal} onClick={() => setShowQRCode(false)}>
          <div className={css.qrContent} onClick={(e) => e.stopPropagation()}>
            <h3><FormattedMessage id="ShareButton.qrTitle" /></h3>
            <img 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url || window.location.href)}`}
              alt="QR Code"
              className={css.qrImage}
            />
            <p className={css.qrHelp}><FormattedMessage id="ShareButton.qrHelp" /></p>
            <button className={css.closeButton} onClick={() => setShowQRCode(false)}>
              <FormattedMessage id="ShareButton.close" />
            </button>
          </div>
        </div>
      )}
      
      {showToast && (
        <div
          className={classNames(css.toast, {
            [css.toastError]: toastError,
            [css.toastShow]: showToast,
          })}
          role="alert"
          aria-live="polite"
        >
          <FormattedMessage id={toastMessage} />
        </div>
      )}
    </>
  );
};

ShareButton.defaultProps = {
  className: null,
  rootClassName: null,
  title: null,
  description: null,
  url: null,
};

ShareButton.propTypes = {
  className: string,
  rootClassName: string,
  title: string,
  description: string,
  url: string,
};

export default ShareButton;
